import UAParser from 'ua-parser-js';

export const DEVICE_FINGERPRINT_HEADER = 'x-device-fingerprint';

const REQUESTS_REQUIRING_DEVICE_FINGERPRINT = [
    { url: '/customers', method: 'post' },
    { url: '/customers/auth', method: 'post' },
    { url: /^\/jp\/promotional-discounts/, method: 'get' },
    { url: '/jp/journey-plan', method: 'post' },
    { url: '/jp/season-tickets', method: 'post' },
    { url: '/orders', method: 'post' },
];

let sessionDeviceFingerprint;

const isPlainObject = (value) =>
    Object.prototype.toString.call(value) === '[object Object]';

const safeRead = (reader, fallback = null) => {
    try {
        const value = reader();
        return value === undefined ? fallback : value;
    } catch (_error) {
        return fallback;
    }
};

const normalizeHardwareConcurrency = (value) => {
    if (typeof value !== 'number') {
        return null;
    }

    if (value <= 2) {
        return 2;
    }

    if (value <= 4) {
        return 4;
    }

    return 8;
};

const sortObjectKeys = (value) => {
    if (Array.isArray(value)) {
        return value.map(sortObjectKeys);
    }

    if (!isPlainObject(value)) {
        return value;
    }

    return Object.keys(value).sort().reduce((sorted, key) => {
        sorted[key] = sortObjectKeys(value[key]);
        return sorted;
    }, {});
};

const hashSegment = (input, seed = 0) => {
    let h1 = 0xdeadbeef ^ seed;
    let h2 = 0x41c6ce57 ^ seed;

    for (let i = 0; i < input.length; i++) {
        const charCode = input.charCodeAt(i);
        h1 = Math.imul(h1 ^ charCode, 2654435761);
        h2 = Math.imul(h2 ^ charCode, 1597334677);
    }

    h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507)
        ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
    h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507)
        ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);

    return (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(36);
};

function collectWebGLFingerprint() {
    const canvas = safeRead(() => document.createElement('canvas'), null);

    if (!canvas || typeof canvas.getContext !== 'function') {
        return null;
    }

    const gl = safeRead(
        () => canvas.getContext('webgl') || canvas.getContext('experimental-webgl'),
        null,
    );

    if (!gl) {
        return null;
    }

    return {
        maxTextureSize: safeRead(() => gl.getParameter(gl.MAX_TEXTURE_SIZE), null),
        maxVertexAttribs: safeRead(() => gl.getParameter(gl.MAX_VERTEX_ATTRIBS), null),
        maxVaryingVectors: safeRead(() => gl.getParameter(gl.MAX_VARYING_VECTORS), null),
        maxVertexUniformVectors: safeRead(() => gl.getParameter(gl.MAX_VERTEX_UNIFORM_VECTORS), null),
        maxFragmentUniformVectors: safeRead(() => gl.getParameter(gl.MAX_FRAGMENT_UNIFORM_VECTORS), null),
    };
}

function collectFingerprintSource() {
    const parser = new UAParser(safeRead(() => navigator.userAgent, ''));
    const device = parser.getDevice();
    const os = parser.getOS();
    const cpu = parser.getCPU();

    return {
        version: 2,
        platform: safeRead(() => navigator.platform, safeRead(() => navigator.userAgentData.platform, null)),
        timezone: safeRead(() => new Intl.DateTimeFormat().resolvedOptions().timeZone, null),
        timezoneOffset: safeRead(() => new Date().getTimezoneOffset(), null),
        hardwareConcurrency: safeRead(() => navigator.hardwareConcurrency, null),
        deviceMemory: safeRead(() => navigator.deviceMemory, null),
        maxTouchPoints: safeRead(() => navigator.maxTouchPoints, null),
        cpuArchitecture: cpu.architecture || null,
        os: {
            name: os.name || null,
            version: os.version || null,
        },
        device: {
            type: device.type || null,
            vendor: device.vendor || null,
            model: device.model || null,
        },
        webgl: collectWebGLFingerprint(),
    };
}

function normalizeFingerprintSource(source) {
    return {
        version: source.version,
        platform: source.platform || null,
        timezone: source.timezone || null,
        timezoneOffset: source.timezoneOffset,
        hardwareConcurrency: normalizeHardwareConcurrency(source.hardwareConcurrency),
        maxTouchPoints: source.maxTouchPoints,
        os: {
            name: safeRead(() => source.os.name, null),
        },
        device: {
            type: safeRead(() => source.device.type, null),
        },
        webgl: {
            maxTextureSize: safeRead(() => source.webgl.maxTextureSize, null),
            maxVertexAttribs: safeRead(() => source.webgl.maxVertexAttribs, null),
            maxVaryingVectors: safeRead(() => source.webgl.maxVaryingVectors, null),
            maxVertexUniformVectors: safeRead(() => source.webgl.maxVertexUniformVectors, null),
            maxFragmentUniformVectors: safeRead(() => source.webgl.maxFragmentUniformVectors, null),
        },
    };
}

export function createDeviceFingerprint(source) {
    try {
        const fingerprintSource = source === undefined ? collectFingerprintSource() : source;
        const normalizedSource = sortObjectKeys(normalizeFingerprintSource(fingerprintSource));
        const serializedSource = JSON.stringify(normalizedSource);
        const hashParts = [
            hashSegment(serializedSource, 0),
            hashSegment(serializedSource, 1),
        ];
        return `dfp-v1-${hashParts[0]}-${hashParts[1]}`;
    } catch (_error) {
        return null;
    }
}

export function requestRequiresDeviceFingerprint(path, method) {
    if (!path || !method) {
        return false;
    }

    const requestMethod = String(method).toLowerCase();
    const requestPath = String(path);
    const normalizedPath = requestPath.split('?')[0];

    return REQUESTS_REQUIRING_DEVICE_FINGERPRINT.some((matcher) => {
        if (requestMethod !== matcher.method.toLowerCase()) {
            return false;
        }

        if (matcher.url instanceof RegExp) {
            return matcher.url.test(requestPath);
        }

        return matcher.url === normalizedPath;
    });
}

export function getSessionDeviceFingerprint() {
    if (sessionDeviceFingerprint === undefined) {
        sessionDeviceFingerprint = createDeviceFingerprint();
    }

    return sessionDeviceFingerprint;
}

const DeviceFingerprint = {
    get: getSessionDeviceFingerprint,
};

export default DeviceFingerprint;
